import { Box, CircularProgress } from '@mui/material';
import { Grid } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MfaRequiredError } from 'src/api/APIClient';
import { useAPI } from 'src/api/provider';
import { StringParam, useQueryParam } from 'src/helpers/queryParam';
import { type PendingMFAChallenge, toPendingMfaChallenge } from '../components/loginMfaChallenge';
import { MfaChallengeForm, type MfaChallengeRetryResult } from '../components/MfaChallengeForm';
import { InvalidMagicLink } from './InvalidMagicLink';
import { buildPostMagicLinkUrl } from './magicLinkUrl';

const ConsumingMagicLink = () => {
    const { t } = useTranslation('auth.magicLink');
    return (
        <Grid
            container
            alignItems="center"
            justifyContent="center"
            direction="column"
            style={{ minHeight: '100vh' }}
        >
            <CircularProgress size={100} />
            <Box my={4}>{t('processingLoginLink')}</Box>
        </Grid>
    );
};

export const MagicLinkConsumer = ({ children }: { children: React.ReactNode }) => {
    const apiClient = useAPI();

    const [loginFailed, setLoginFailed] = useState(false);
    const [pendingMfaChallenge, setPendingMfaChallenge] = useState<PendingMFAChallenge | null>(
        null,
    );
    const [username] = useQueryParam('username', StringParam);
    const [token] = useQueryParam('magic_link_token', StringParam);

    const redirectAfterMagicLink = useCallback(() => {
        window.location.replace(
            buildPostMagicLinkUrl({
                pathname: window.location.pathname,
                search: window.location.search,
                hash: window.location.hash,
            }),
        );
    }, []);

    const requestMagicLinkLogin = useCallback(async (): Promise<MfaChallengeRetryResult> => {
        if (!token) {
            return { outcome: 'failed' };
        }
        try {
            await apiClient.post('/magic-link-login/', { token });
            redirectAfterMagicLink();
            return { outcome: 'verified' };
        } catch (err: any) {
            const challenge =
                err instanceof MfaRequiredError ? toPendingMfaChallenge(err, username || '') : null;
            if (!challenge) {
                return { outcome: 'failed' };
            }
            setPendingMfaChallenge(challenge);
            return { outcome: 'challenge', challenge };
        }
    }, [apiClient, redirectAfterMagicLink, token, username]);

    useEffect(() => {
        void (async () => {
            if (!token) {
                return;
            }
            const result = await requestMagicLinkLogin();
            if (result.outcome === 'failed') {
                setLoginFailed(true);
            }
        })();
    }, [requestMagicLinkLogin, token]);

    const failMagicLinkLogin = () => {
        setPendingMfaChallenge(null);
        setLoginFailed(true);
    };

    if (pendingMfaChallenge) {
        return (
            <Grid
                container
                alignItems="center"
                justifyContent="center"
                direction="column"
                style={{ minHeight: '100vh' }}
            >
                <Box width="100%" maxWidth={420}>
                    <MfaChallengeForm
                        challenge={pendingMfaChallenge}
                        onVerified={redirectAfterMagicLink}
                        onRetryChallenge={async () => {
                            const result = await requestMagicLinkLogin();
                            if (result.outcome === 'failed') {
                                failMagicLinkLogin();
                            }
                            return result;
                        }}
                    />
                </Box>
            </Grid>
        );
    }

    if (token) {
        return loginFailed ? (
            <InvalidMagicLink initialUsername={username || ''} />
        ) : (
            <ConsumingMagicLink />
        );
    }

    return <>{children}</>;
};
